<template>
	<div v-if="cartData && cmsUpperPaymentMethods" class="payment">
		<div class="heading-1">{{ $t('paymentTitle') }}</div>
		<div class="body-text-1">{{ $t('paymentSubTitle') }}</div>

		<div class="container-wrapper">
			<div class="container-border top"></div>
			<div class="jagged-container">
				<div class="shoppingBreakdown-container">
					<div v-if="cartData.length" class="shopping-breakdown">
						<h2>{{ $t('costBreakdown') }}</h2>
						<div
							v-for="expense in filteredCartDataWithMonthlyLoanAmount"
							:key="expense.expenseSubCategory_ID"
						>
							<div class="subcat-info body-text-1">
								<div class="subcat-name">
									<svgicon
										v-if="expense.isMandatory"
										class="icon--small icon--danger"
										name="info"
									/>
									{{
										`${expense.adultQuantity + expense.childQuantity} x ${
											expense.name
										}`
									}}
								</div>
								<div class="subcat-cost">
									{{ expense.cost | currency }}
									<span v-if="!paymentCompleted" class="remove-icon">
										<svgicon
											class="icon--clickable icon--medium icon--danger"
											name="delete"
											@click="removeItemFromCart(expense.expenseSubCategory_ID)"
										/>
									</span>
								</div>
							</div>
						</div>
						<ja-payment-types
							:options="loanPaymentOptions"
							:selected-option="selectedPaymentMethod"
							:disabled="paymentLoading || paymentCompleted"
							:option-default="optionPreSelected"
							@optionSelected="onOptionSelected"
							@optionDeselected="onOptionDeselected"
						></ja-payment-types>
					</div>
					<div
						class="total-cost"
						:class="{ overflow: totalCost > categoryBudget }"
					>
						<h3>{{ totalCostMessage }}</h3>
						<h3>{{ totalCost | currency }}</h3>
					</div>
					<div class="shopping-breakdown">
						<div
							v-for="expense in cartData"
							:key="expense.expenseSubCategory_ID"
						>
							<div class="subcat-info body-text-1">
								<div class="subcat-name">
									<svgicon
										v-if="expense.isMandatory"
										class="icon--small icon--danger"
										name="info"
									/>
									{{ `${getQuantity(expense)} x ${expense.name}` }}
								</div>
								<div class="subcat-cost">
									{{ expense.totalCost | currency }}
									<span
										v-if="!expense.monthlyLoanAmount && !paymentCompleted"
										class="remove-icon"
									>
										<svgicon
											class="icon--clickable icon--medium icon--danger"
											name="delete"
											@click="removeItemFromCart(expense.expenseSubCategory_ID)"
										/>
									</span>
								</div>
							</div>
						</div>
					</div>
					<div
						v-if="haveToUseLoan"
						class="total-cost"
						:class="{ overflow: totalCost > categoryBudget }"
					>
						<h3>{{ $t('total') }}</h3>
						<h3>{{ totalCost | currency }}</h3>
					</div>
					<div class="discard-panel">
						<button
							v-if="!paymentCompleted && cartData.length >= 2"
							class="btn btn-primary btn-link btn--bold"
							@click="discardAll"
						>
							{{ $t('discardAllPurchases') }}
						</button>
					</div>
					<div v-if="optionsData.length" class="payment-method">
						<ja-payment-types
							:options="checkingAndCreditCardPaymentOptions"
							:selected-option="selectedPaymentMethod"
							:disabled="paymentLoading || paymentCompleted"
							:option-default="optionPreSelected"
							@optionSelected="onOptionSelected"
							@optionDeselected="onOptionDeselected"
						></ja-payment-types>
					</div>
					<div class="payment-button-container">
						<div
							v-if="
								alertForMarriedSelectedTwoTransports ||
									(!requiredCategoriesShopped && selectedPaymentMethod)
							"
							class="alert-payment heading-5 mb--2 text--success text--center max-width--70"
						>
							<span>{{ errorMessage }}</span>
						</div>
						<ja-button
							class="large"
							name="pay"
							:success="paymentCompleted"
							:tabindex="paymentCompleted ? -1 : 0"
							:aria-label="
								$t('payWithMethod', {
									method: selectedPaymentMethod
										? selectedPaymentMethod.name
										: '',
									amount: totalCost
								})
							"
							:disabled="
								cartData.length &&
									(!selectedPaymentMethod ||
										!requiredCategoriesShopped ||
										paymentLoading ||
										ifMarriedSelectTwoModesOfTransportation ||
										isDeselected)
							"
							@click.native.once="checkAndUpdate"
						>
							<span class="button-text">{{ $t('payButton') }}</span>
						</ja-button>
					</div>
				</div>
			</div>

			<div class="container-border bottom"></div>
		</div>
	</div>
</template>
<script>
import gql from 'graphql-tag';
import { LifeStageData, ProfileData, UserData } from '@/mixins';
import Button from '../ui/Button';
import PaymentTypes from './PaymentTypes';
import StudentsService from '@/services/StudentsService';
import ExpenseCategoryService from '@/services/ExpenseCategoryService';

import {
	applicationLoanTypes,
	defaultNames,
	loanApplicableCategories,
	maritalStatus,
	simulationNameType
} from '@/helpers/constants';

export default {
	components: {
		jaButton: Button,
		jaPaymentTypes: PaymentTypes
	},
	mixins: [LifeStageData, ProfileData, UserData],
	props: {
		cartData: {
			type: Array,
			default() {
				return [];
			}
		},
		requiredCategories: {
			type: Array,
			default() {
				return [];
			}
		},
		totalCost: {
			type: Number,
			default: 0
		},
		categoryBudget: {
			type: Number,
			default: 0
		},
		allSubcategories: {
			type: Object,
			default: () => {}
		},
		preselectedPaymentMethod: {
			type: Number,
			default: null
		},
		preselectedItems: {
			type: Array,
			default: () => []
		},
		skippedPurchaseId: {
			type: Number,
			default: 0
		},
		haveToUseLoan: {
			type: Boolean,
			default: false
		},
		loanType: {
			type: String,
			default: ''
		},
		category: {
			type: Object,
			default: null
		},
		autoLoanInterestSelection: {
			type: Object,
			default: () => {}
		}
	},
	data() {
		return {
			cmsUpperPaymentMethods: [],
			selectedPaymentMethod: [],
			paymentLoading: false,
			paymentCompleted: false,
			deletionIds: [],
			JA_LOCALIZATION_IRL: this.$store.getters.isIrl,
			isButtonDisabled: true,
			alertForMarriedSelectedTwoTransports: false,
			errorMessage: '',
			isDeselected: true,
			optionDeselected: null,
			simulationType: null,
			optionPreSelected: null,
			numItems: 0
		};
	},
	apollo: {
		cmsUpperPaymentMethods: {
			query: gql`
				{
					cmsUpperPaymentMethods {
						name
						defaultName
						upperPaymentMethod_ID
					}
				}
			`,
			update(data) {
				this.$store.commit('setLoading', false);
				return data.cmsUpperPaymentMethods;
			}
		}
	},
	computed: {
		constants() {
			return this.$store.getters.constants;
		},
		TEENAGER_LIFE_STAGE_ID() {
			if (this.constants) {
				return this.constants.teenagerAdultLifeStageGuid;
			}
			return null;
		},
		requiredCategoriesShopped() {
			let requiredCounter = 0;

			this.requiredCategories.forEach(subcategoryID => {
				for (let i = 0, len = this.cartData.length; i < len; i++) {
					if (subcategoryID == this.cartData[i].expenseSubCategory_ID) {
						requiredCounter++;
						break;
					}
				}
			});

			this.ifCategoryMandatory();

			return this.requiredCategories.length == requiredCounter;
		},
		ifMarriedSelectTwoModesOfTransportation() {
			if (this.selectedPaymentMethod === null) {
				return true;
			}

			const hasNonEmptyLoanAmount = this.cartData.filter(
				item => item.monthlyLoanAmount !== null
			);

			const allItemsHaveEmptyLoanAmount = this.cartData.filter(
				item => item.monthlyLoanAmount === null
			);

			const twoSelectedMethods = this.selectedPaymentMethod.length === 2;

			const twoSelectedCart = this.cartData.every(
				card => card.adultQuantity >= 2
			);

			const selectedMethodIsAutoLoan = this.selectedPaymentMethod.some(method =>
				method.defaultName.includes(defaultNames.AUTO_LOAN)
			);

			const selectedMethodIsMortgageLoan = this.selectedPaymentMethod.some(p =>
				p.defaultName.includes(defaultNames.MORTGAGE_LOAN)
			);
			if (
				this.category.assetName.toLowerCase() ===
					loanApplicableCategories.TRANSPORTATION &&
				(this.profileData.cmsMaritalStatus.status.toLowerCase() ===
					this.$t('married') ||
					this.profileData.cmsMaritalStatus.status.toLowerCase() ===
						maritalStatus.MARRIED.toLowerCase())
			) {
				if (
					(hasNonEmptyLoanAmount.length >= 2 &&
						this.cartData.length >= 2 &&
						!allItemsHaveEmptyLoanAmount.length) ||
					twoSelectedCart
				) {
					this.messageError(this.cartData);
					return false;
				} else if (
					(hasNonEmptyLoanAmount.length >= 2 &&
						this.cartData.length >= 2 &&
						!!allItemsHaveEmptyLoanAmount.length &&
						twoSelectedMethods) ||
					twoSelectedCart
				) {
					this.messageError(this.cartData);
					return false;
				} else if (
					(hasNonEmptyLoanAmount.length >= 1 &&
						twoSelectedMethods &&
						hasNonEmptyLoanAmount.length >= 1 &&
						this.cartData.length >= 2) ||
					twoSelectedCart
				) {
					this.messageError(this.cartData);
					return false;
				} else if (
					(this.cartData.length >= 2 &&
						this.selectedPaymentMethod.length === 1 &&
						!selectedMethodIsAutoLoan) ||
					twoSelectedCart
				) {
					this.messageError(this.cartData);
					return false;
				} else if (this.cartData.length === 1 && !twoSelectedCart) {
					this.messageError(this.cartData);
					return true;
				} else if (
					this.cartData.length >= 2 &&
					hasNonEmptyLoanAmount.length === 0
				) {
					this.messageError(this.cartData);
					return false;
				}
			} else if (
				(this.profileData.cmsMaritalStatus.status.toLowerCase() !==
					this.$t('married') ||
					this.profileData.cmsMaritalStatus.status.toLowerCase() !==
						maritalStatus.MARRIED.toLowerCase()) &&
				selectedMethodIsAutoLoan &&
				!allItemsHaveEmptyLoanAmount.length &&
				twoSelectedMethods
			) {
				return false;
			} else if (
				(this.profileData.cmsMaritalStatus.status.toLowerCase() !==
					this.$t('married') ||
					this.profileData.cmsMaritalStatus.status.toLowerCase() !==
						maritalStatus.MARRIED.toLowerCase()) &&
				selectedMethodIsAutoLoan &&
				allItemsHaveEmptyLoanAmount.length >= 1 &&
				twoSelectedMethods
			) {
				return false;
			} else if (
				(this.profileData.cmsMaritalStatus.status.toLowerCase() !==
					this.$t('married') ||
					this.profileData.cmsMaritalStatus.status.toLowerCase() !==
						maritalStatus.MARRIED.toLowerCase()) &&
				!selectedMethodIsAutoLoan &&
				!selectedMethodIsMortgageLoan &&
				(this.isCheckingAccountEnabled() || this.isCreditCardEnabled())
			) {
				return false;
			} else if (
				this.category.assetName.toLowerCase() ===
					loanApplicableCategories.HOUSING &&
				selectedMethodIsMortgageLoan &&
				twoSelectedMethods
			) {
				return false;
			} else if (
				this.category.assetName.toLowerCase() ===
					loanApplicableCategories.HOUSING &&
				!selectedMethodIsMortgageLoan
			) {
				return false;
			} else if (
				this.category.assetName.toLowerCase() !==
					loanApplicableCategories.TRANSPORTATION &&
				this.category.assetName.toLowerCase() !==
					loanApplicableCategories.HOUSING
			) {
				return false;
			}
			return true;
		},
		isTeenagerLifeStage() {
			return this.lifeStageData.lifeStage_ID === this.TEENAGER_LIFE_STAGE_ID;
		},
		optionsData() {
			if (this.lifeStageData && this.cartData.length) {
				const paymentMethods = [];

				this.cmsUpperPaymentMethods.forEach(obj => {
					if (obj.defaultName === defaultNames.CHECKING_ACCOUNT) {
						paymentMethods.push({
							name: `${
								this.JA_LOCALIZATION_IRL ? this.$t('debitAccount') : obj.name
							}`,
							defaultName: obj.defaultName,
							value: obj.upperPaymentMethod_ID,
							amount: this.availableBalance,
							accountEnabled: this.isCheckingAccountEnabled(),
							balanceNotEnough: this.isCheckingBalanceNotEnough()
						});
					}
					if (
						obj.defaultName === defaultNames.MORTGAGE_LOAN &&
						this.isMortgageLoanPaymentVisible
					) {
						paymentMethods.push({
							name: obj.name,
							defaultName: obj.defaultName,
							value: obj.upperPaymentMethod_ID,
							amount: this.lifeStageData.mortgageLoanAmount,
							accountEnabled: this.isMortgageLoanEnabled(),
							balanceNotEnough: this.isLoansBalanceDisabled(
								applicationLoanTypes.MORTGAGE_LOAN
							)
						});
					}
					// TODO: Refactor this whole computed.
					// Should be unified instead of if-blocks in a loop
					if (
						obj.defaultName === defaultNames.AUTO_LOAN_DEALERSHIP &&
						this.isAutoLoanPaymentVisible
					) {
						paymentMethods.push({
							name: obj.name,
							defaultName: obj.defaultName,
							value: obj.upperPaymentMethod_ID,
							amount: this.lifeStageData.autoLoanDealershipAmount,
							accountEnabled: this.isAutoLoanEnabled(),
							balanceNotEnough: this.isLoansBalanceDisabled(
								applicationLoanTypes.AUTO_LOAN
							)
						});
					}
					if (
						obj.defaultName === defaultNames.AUTO_LOAN &&
						this.isAutoLoanBankingPaymentVisible
					) {
						paymentMethods.push({
							name: this.$t('autoLoanBankingAndFinance'),
							value: obj.upperPaymentMethod_ID,
							defaultName: obj.defaultName,
							amount: this.lifeStageData.autoLoanAmount,
							accountEnabled: this.isAutoLoanBankingEnabled(),
							balanceNotEnough: this.isLoansBalanceDisabled(
								applicationLoanTypes.AUTO_LOAN_BANKING_AND_FINANCE
							)
						});
					}

					if (
						obj.defaultName == defaultNames.CREDIT_CARD &&
						this.profileData.simulation.isCreditCardEnable
					) {
						paymentMethods.push({
							name: obj.name,
							defaultName: obj.defaultName,
							value: obj.upperPaymentMethod_ID,
							amount: this.debts.creditCard.balance,
							accountEnabled: this.isCreditCardEnabled(),
							balanceNotEnough:
								!this.isTeenagerLifeStage &&
								this.debts.creditCard.balance < this.totalCost
						});
					}
				});
				return paymentMethods;
			}

			return [];
		},
		loanPaymentOptions() {
			if (this.lifeStageData && this.cartData.length) {
				const paymentMethods = [];

				this.cmsUpperPaymentMethods.forEach(obj => {
					if (
						obj.defaultName === defaultNames.MORTGAGE_LOAN &&
						this.isMortgageLoanPaymentVisible
					) {
						paymentMethods.push({
							name: obj.name,
							defaultName: obj.defaultName,
							value: obj.upperPaymentMethod_ID,
							amount: this.lifeStageData.mortgageLoanAmount,
							accountEnabled: this.isMortgageLoanEnabled(),
							balanceNotEnough: this.isLoansBalanceDisabled(
								applicationLoanTypes.MORTGAGE_LOAN
							)
						});
					}
					// TODO: Refactor this whole computed.
					// Should be unified instead of if-blocks in a loop
					if (
						obj.defaultName === defaultNames.AUTO_LOAN_DEALERSHIP &&
						this.isAutoLoanPaymentVisible &&
						this.autoLoanInterestSelection &&
						this.autoLoanInterestSelection.defaultName ===
							defaultNames.AUTO_LOAN_DEALERSHIP
					) {
						paymentMethods.push({
							name: obj.name,
							defaultName: obj.defaultName,
							value: obj.upperPaymentMethod_ID,
							amount: this.lifeStageData.autoLoanDealershipAmount,
							accountEnabled: this.isAutoLoanEnabled(),
							balanceNotEnough: this.isLoansBalanceDisabled(
								applicationLoanTypes.AUTO_LOAN
							)
						});
					}
					if (
						obj.defaultName === defaultNames.AUTO_LOAN &&
						this.isAutoLoanBankingPaymentVisible &&
						this.autoLoanInterestSelection &&
						this.autoLoanInterestSelection.defaultName ===
							defaultNames.AUTO_LOAN
					) {
						paymentMethods.push({
							name: this.$t('autoLoanBankingAndFinance'),
							value: obj.upperPaymentMethod_ID,
							defaultName: obj.defaultName,
							amount: this.lifeStageData.autoLoanAmount,
							accountEnabled: this.isAutoLoanBankingEnabled(),
							balanceNotEnough: this.isLoansBalanceDisabled(
								applicationLoanTypes.AUTO_LOAN_BANKING_AND_FINANCE
							)
						});
					}
				});
				return paymentMethods;
			}

			return [];
		},
		checkingAndCreditCardPaymentOptions() {
			if (this.lifeStageData && this.cartData.length) {
				const paymentMethods = [];

				this.cmsUpperPaymentMethods.forEach(obj => {
					if (obj.defaultName === defaultNames.CHECKING_ACCOUNT) {
						paymentMethods.push({
							name: `${
								this.JA_LOCALIZATION_IRL ? this.$t('debitAccount') : obj.name
							}`,
							defaultName: obj.defaultName,
							value: obj.upperPaymentMethod_ID,
							amount: this.availableBalance,
							accountEnabled: this.isCheckingAccountEnabled(),
							balanceNotEnough: this.isCheckingBalanceNotEnough()
						});
					}
					if (
						obj.defaultName == defaultNames.CREDIT_CARD &&
						this.profileData.simulation.isCreditCardEnable
					) {
						paymentMethods.push({
							name: obj.name,
							defaultName: obj.defaultName,
							value: obj.upperPaymentMethod_ID,
							amount: this.debts.creditCard.balance,
							accountEnabled: this.isCreditCardEnabled(),
							balanceNotEnough:
								!this.isTeenagerLifeStage &&
								this.debts.creditCard.balance < this.totalCost
						});
					}
				});
				return paymentMethods;
			}

			return [];
		},
		availableBalance() {
			if (this.lifeStageData) {
				return this.netMonthlyIncome - this.lifeStageData.totalExpenses;
			}
			return null;
		},
		creditBalance() {
			if (this.lifeStageData && this.profileData) {
				return (
					this.profileData.cmsLifeStage.creditCardLimit -
					this.lifeStageData.creditCardDebt
				);
			}
			return null;
		},
		debts() {
			return this.$store.getters.debts;
		},
		totalCostForLoans() {
			// Used to include cost of an item instead of totalCost
			// This is needed only for validation purposes
			// In other cases totalCost (prop) is used
			return this.cartData.reduce((sum, item) => {
				if (this.haveToUseLoan && item.monthlyLoanAmount) {
					return sum + parseInt(item.cost);
				}

				if (item.totalCost >= 0) {
					return sum + parseInt(item.totalCost);
				}
			}, 0);
		},
		isAutoLoanPaymentVisible() {
			const isMonthlyLoanAmountNotNull = this.cartData.some(
				entry => entry.monthlyLoanAmount !== null
			);

			return (
				isMonthlyLoanAmountNotNull &&
				this.haveToUseLoan &&
				this.$store.getters.autoLoan.hasApplied &&
				(this.loanType === applicationLoanTypes.AUTO_LOAN ||
					this.loanType === applicationLoanTypes.AUTO_LOAN_BANKING_AND_FINANCE)
			);
		},
		isAutoLoanBankingPaymentVisible() {
			const isMonthlyLoanAmountNotNull = this.cartData.some(
				entry => entry.monthlyLoanAmount !== null
			);
			return (
				isMonthlyLoanAmountNotNull &&
				this.haveToUseLoan &&
				this.$store.getters.autoLoanBankingAndFinance.hasApplied &&
				(this.loanType === applicationLoanTypes.AUTO_LOAN ||
					this.loanType === applicationLoanTypes.AUTO_LOAN_BANKING_AND_FINANCE)
			);
		},
		isMortgageLoanPaymentVisible() {
			return (
				this.haveToUseLoan &&
				this.$store.getters.mortgageLoan.hasApplied &&
				this.loanType === applicationLoanTypes.MORTGAGE_LOAN
			);
		},
		totalCostMessage() {
			return this.haveToUseLoan
				? this.$t('totalMonthlyCostMessage')
				: this.$t('totalCostMessage');
		},
		filteredCartDataWithMonthlyLoanAmount() {
			return this.cartData.filter(
				expense => expense.monthlyLoanAmount !== null
			);
		},
		filteredCartDataWithoutMonthlyLoanAmount() {
			return this.cartData.filter(
				expense => expense.monthlyLoanAmount === null
			);
		}
	},
	watch: {
		totalCost(value) {
			setTimeout(() => {
				this.$announcer.polite(
					this.$t('totalCostSelection', {
						cost: value,
						budget: this.categoryBudget
					})
				);
			}, 200);
		},
		cartData: {
			handler() {
				if (!this.cartData.length) {
					this.selectedPaymentMethod = [];
				} else {
					if (!this.numItems) {
						this.numItems = this.cartData.length;
					} else {
						this.onOptionDeselected(this.optionPreSelected);
						this.optionPreSelected = null;
						this.selectedPaymentMethod = [];
					}
				}
				if (
					this.profileData.cmsMaritalStatus.status.toLowerCase() ===
					this.$t('married')
				) {
					this.messageError(this.cartData);
				}
			},
			immediate: true,
			deep: true
		},
		paymentLoading() {
			this.$emit('paymentLoading', this.paymentLoading);
		},
		preselectedItems(value) {
			this.deletionIds = value;
		},
		lifeStageData(data) {
			if (data === null) {
				this.$store.commit('setLoading', true);
				this.$store.dispatch('getLifeStageData');
			} else {
				this.$store.commit('setLoading', false);
			}
		},
		preselectedPaymentMethod(id) {
			const preSelectedPayment = this.cmsUpperPaymentMethods.find(
				method => method.upperPaymentMethod_ID === id
			);
			const updatedPayment = {
				...preSelectedPayment,
				value: id
			};
			this.optionPreSelected = updatedPayment;
			this.onOptionSelected([updatedPayment]);
		}
	},
	mounted() {
		this.simulationType = this.$store.getters.simulationType;
	},
	methods: {
		getQuantity(expense) {
			return expense.adultQuantity + expense.childQuantity;
		},
		messageError(cartData) {
			const twoSelectedCart = cartData.every(card => card.adultQuantity >= 2);
			if (
				cartData.length === 1 &&
				!twoSelectedCart &&
				this.category.assetName.toLowerCase() ===
					loanApplicableCategories.TRANSPORTATION
			) {
				this.errorMessage = this.$t('errorMessageTransportation');
				this.alertForMarriedSelectedTwoTransports = true;
			} else {
				this.alertForMarriedSelectedTwoTransports = false;
			}
		},
		ifCategoryMandatory() {
			this.errorMessage = this.$t('errorMessageIsMandatory');
		},
		onOptionSelected(method) {
			const isAutoLoanSelected = this.selectedPaymentMethod.some(methodItem =>
				methodItem.defaultName.includes(defaultNames.AUTO_LOAN)
			);
			if (method.length > 0) {
				const isMethodAlreadySelected = this.selectedPaymentMethod.some(
					selectedMethod => selectedMethod.defaultName === method[0].defaultName
				);

				if (!isMethodAlreadySelected) {
					if (this.selectedPaymentMethod.length < 2) {
						this.selectedPaymentMethod.push(method[0]);
					} else if (
						this.selectedPaymentMethod.length === 2 &&
						isAutoLoanSelected
					) {
						const existingMethodIndex = this.selectedPaymentMethod.findIndex(
							selectedMethod =>
								!selectedMethod.defaultName.includes(defaultNames.AUTO_LOAN)
						);

						if (existingMethodIndex !== -1) {
							this.selectedPaymentMethod.splice(
								existingMethodIndex,
								1,
								method[0]
							);
						}
					}
				}
			} else if (this.optionDeselected != null) {
				const defaultNameDesSelected = this.optionDeselected.defaultName;

				const deselectedIndex = this.selectedPaymentMethod.findIndex(
					selectedMethod =>
						selectedMethod.defaultName === defaultNameDesSelected
				);

				if (deselectedIndex !== -1) {
					this.selectedPaymentMethod.splice(deselectedIndex, 1);
				}
				if (defaultNameDesSelected.includes(defaultNames.AUTO_LOAN)) {
					this.selectedPaymentMethod = [];
				}
			}

			if (this.selectedPaymentMethod.length >= 1) {
				this.isDeselected = false;
			}
		},

		onOptionDeselected(option) {
			this.optionDeselected = option;
			this.isDeselected = true;
		},
		async checkAndUpdate() {
			this.paymentLoading = true;
			// TODO: Refactor this.Use external function to avoid code repetition
			// remap the categories to get only the IDs.
			const subcategoriesIds = this.allSubcategories.cmsExpenseSubCategories.map(
				s => s.expenseSubCategory_ID
			);

			// If second tab is opened preselectedItems won't be loaded
			// Since it is a first purchase for a category
			// This will guarantee that if a user is already purchased something (in other tab)
			// The ids will be fetched and passed to payment mutation
			const shouldReturnIds = this.preselectedItems.length ? false : true;

			// if preselectedItems are available there is no need of this request
			if (shouldReturnIds) {
				// Make request to check if current category is already completed
				const isAbleToProceedCorrectly = await StudentsService.checkBeforeUpdate(
					this.userData.studentId,
					this.userData.studentLifeStageId,
					subcategoriesIds,
					this.allSubcategories.expenseCategory_ID,
					true
				);

				// if it is already completed then we have to update
				// the Ids for deletion and set new values (even if they are the same)
				if (Array.isArray(isAbleToProceedCorrectly.subCategories)) {
					this.deletionIds = isAbleToProceedCorrectly;
				}
			}
			this.commitPayment();
		},
		hasDisallowedItems() {
			// if there is Item without monthly Loan Amount
			// We have to disable the Loan Payment Option payment option
			return !this.cartData.every(i => !!i.monthlyLoanAmount);
		},
		hasLoanItems() {
			// Consider monthlyLoanAmount in SOME items only if
			// loans are available for current SimType
			if (this.areLoansAvailableBySimulation) {
				return this.cartData.some(i => !!i.monthlyLoanAmount);
			}

			return false;
		},
		isMortgageLoanEnabled() {
			return (
				!this.hasDisallowedItems() &&
				// INFO: Remove this line to enable more than one item to be purchased
				// with auto loan
				this.cartData.length === 1 &&
				this.$store.getters.mortgageLoan.hasApplied &&
				!this.isLoansBalanceDisabled(applicationLoanTypes.MORTGAGE_LOAN)
			);
		},
		isAutoLoanEnabled() {
			return (
				// INFO: Remove this line to enable more than one item to be purchased
				// with auto loan
				this.cartData.filter(i => !!i.monthlyLoanAmount) &&
				this.$store.getters.autoLoan.hasApplied &&
				!this.isLoansBalanceDisabled(applicationLoanTypes.AUTO_LOAN)
			);
		},
		isAutoLoanBankingEnabled() {
			return (
				// INFO: Remove this line to enable more than one item to be purchased
				// with auto loan
				this.cartData.filter(i => !!i.monthlyLoanAmount) &&
				this.$store.getters.autoLoanBankingAndFinance.hasApplied &&
				!this.isLoansBalanceDisabled(
					applicationLoanTypes.AUTO_LOAN_BANKING_AND_FINANCE
				)
			);
		},
		isLoansBalanceDisabled(loanType) {
			const reduced = this.cartData
				.filter(i => !!i.monthlyLoanAmount)
				.reduce((sum, i) => (sum += parseInt(i.cost)), 0);

			if (loanType === applicationLoanTypes.AUTO_LOAN) {
				return this.lifeStageData.autoLoanDealershipAmount < reduced;
			}
			if (loanType === applicationLoanTypes.AUTO_LOAN_BANKING_AND_FINANCE) {
				return this.lifeStageData.autoLoanAmount < reduced;
			}
			return this.lifeStageData.mortgageLoanAmount < reduced;
		},
		async commitPayment() {
			if (!this.paymentCompleted && this.selectedPaymentMethod) {
				this.$announcer.polite(this.$t('paymentLoading'));

				let paymentMethodID = this.selectedPaymentMethod;

				const defaultPaymentMethod = paymentMethodID.find(
					p => !p.defaultName.includes(defaultNames.AUTO_LOAN)
				);
				const autoLoanPaymentMethod = paymentMethodID.find(p =>
					p.defaultName.includes(defaultNames.AUTO_LOAN)
				);
				const mortgageLoanPaymentMethod = paymentMethodID.find(p =>
					p.defaultName.includes(defaultNames.MORTGAGE_LOAN)
				);
				const isAutoLoanSelected = autoLoanPaymentMethod !== undefined;

				let subCategories = this.cartData.map(expense => {
					let upperPaymentMethod_ID = defaultPaymentMethod.value;

					if (expense.monthlyLoanAmount != null && isAutoLoanSelected) {
						upperPaymentMethod_ID = autoLoanPaymentMethod.value;
					}
					if (mortgageLoanPaymentMethod) {
						upperPaymentMethod_ID = defaultPaymentMethod.value;
					} else if (expense.monthlyLoanAmount === null) {
						upperPaymentMethod_ID = defaultPaymentMethod.value;
					}

					return {
						expenseSubCategory_Id: expense.expenseSubCategory_ID,
						studentId: this.lifeStageData.id,
						adultCount: expense.adultQuantity,
						childCount: expense.childQuantity,
						lifeStage_ID: this.lifeStageData.lifeStage_ID,
						upperPaymentMethod_ID: upperPaymentMethod_ID
					};
				});

				const result = await ExpenseCategoryService.makePurchase(
					subCategories,
					this.deletionIds,
					this.skippedPurchaseId
				);

				if (result) {
					this.paymentCompleted = true;
					this.paymentLoading = false;
					this.$emit('paymentCompleted');

					this.$announcer.polite(this.$t('paymentCompleted'));
				}
			}
		},
		removeItemFromCart(subId) {
			this.$emit('removeItemFromCart', subId);
		},
		discardAll() {
			this.$emit('discardAllPurchases');
		},
		isCheckingAccountEnabled() {
			const hasNonEmptyLoanAmount = this.cartData.filter(
				item => item.monthlyLoanAmount !== null
			);
			const allItemsHaveEmptyLoanAmount = this.cartData.filter(
				item => item.monthlyLoanAmount === null
			);

			const isSelectAutoLoan = (this.selectedPaymentMethod ?? []).some(p =>
				p.defaultName.includes(defaultNames.AUTO_LOAN)
			);
			const isSelectMortgageLoan = (this.selectedPaymentMethod ?? []).some(p =>
				p.defaultName.includes(defaultNames.MORTGAGE_LOAN)
			);

			if (
				this.simulationType === simulationNameType.ADVANCED_VIRTUAL ||
				this.simulationType === simulationNameType.ENTRY_VIRTUAL
			) {
				return this.availableBalance >= this.totalCost;
			}
			if (
				this.loanType === applicationLoanTypes.MORTGAGE_LOAN &&
				isSelectMortgageLoan &&
				this.cartData.length === 1
			) {
				return this.availableBalance >= this.totalCost;
			}
			if (
				this.loanType === applicationLoanTypes.AUTO_LOAN &&
				isSelectAutoLoan
			) {
				return this.availableBalance >= this.totalCost;
			}
			if (
				hasNonEmptyLoanAmount.length >= 1 &&
				this.cartData.length > 1 &&
				allItemsHaveEmptyLoanAmount.length >= 1 &&
				isSelectAutoLoan
			) {
				return this.availableBalance >= this.totalCost;
			} else if (
				hasNonEmptyLoanAmount.length >= 1 &&
				this.availableBalance >= this.totalCostForLoans
			) {
				return this.availableBalance >= this.totalCostForLoans;
			} else if (
				!hasNonEmptyLoanAmount.length &&
				this.availableBalance >= this.totalCostForLoans
			) {
				// INFO: used the standard validation only for mortgage Loan
				// If it is needed to enable checking account for Mortgage Loan as well
				// Delete this if-block
				return (
					this.availableBalance >= this.totalCostForLoans &&
					this.cartData.some(i => i.monthlyLoanAmount === null)
				);
			} else {
				return (
					this.availableBalance >= this.totalCostForLoans &&
					this.cartData.some(i => i.monthlyLoanAmount === null)
				);
			}
		},
		isCreditCardEnabled() {
			const hasNonEmptyLoanAmount = this.cartData.filter(
				item => item.monthlyLoanAmount !== null
			);

			const isSelectAutoLoan = (this.selectedPaymentMethod ?? []).some(p =>
				p.defaultName.includes(defaultNames.AUTO_LOAN)
			);

			if (!this.profileData.simulation.isCreditCardEnable) {
				return this.profileData.simulation.isCreditCardEnable;
			}
			if (isSelectAutoLoan) {
				return false;
			} else if (
				hasNonEmptyLoanAmount.length >= 1 &&
				this.debts.creditCard.balance >= this.totalCostForLoans
			) {
				return this.debts.creditCard.balance >= this.totalCostForLoans;
			} else if (
				!hasNonEmptyLoanAmount.length &&
				this.debts.creditCard.balance >= this.totalCostForLoans
			) {
				// INFO: used the standard validation only for mortgage Loan
				// If it is needed to enable checking account for Mortgage Loan as well
				// Delete this if-block
				if (
					this.loanType === applicationLoanTypes.MORTGAGE_LOAN &&
					hasNonEmptyLoanAmount.length >= 1
				) {
					return (
						!this.hasLoanItems() && this.availableBalance >= this.totalCost
					);
				}
				return (
					this.cartData.some(i => i.monthlyLoanAmount === null) &&
					!this.isTeenagerLifeStage &&
					this.debts.creditCard.balance >= this.totalCostForLoans
				);
			} else {
				return (
					this.cartData.some(i => i.monthlyLoanAmount === null) &&
					!this.isTeenagerLifeStage &&
					this.debts.creditCard.balance >= this.totalCostForLoans
				);
			}
		},
		isCheckingBalanceNotEnough() {
			let cost = this.totalCost;

			return this.availableBalance < cost;
		}
	}
};
</script>
<style lang="scss" scoped>
.payment {
	width: 100%;
	text-align: center;

	.heading-1 {
		margin-top: 0;
		margin-bottom: 16px;
	}

	.body-text-1 {
		padding-bottom: 32px;
	}

	.container-wrapper {
		max-width: 768px;
		width: 75%;
		margin: 17.5px auto;
		position: relative;
		.container-border {
			position: absolute;
			background-image: url('../../assets/images/rombs.png');
			height: 35px;
			width: 100%;

			&.top {
				top: -17.5px;
			}

			&.bottom {
				bottom: -17.5px;
			}
		}

		.jagged-container {
			width: 100%;
			padding: 32px;
			background-color: #f3f8ff;

			.shoppingBreakdown-container {
				.shopping-breakdown {
					padding: 0 48px;
					border-bottom: 1px solid #dfe9f4;

					h2 {
						margin-top: 0;
					}

					.subcat-info {
						display: flex;
						justify-content: space-between;
						padding-bottom: 8px;
						.subcat-purchase {
							font-weight: bold;
							font-size: 20px;
							text-align: left;
						}
						.subcat-purchase-amount {
							font-style: italic;
							font-size: 20px;
							text-align: end;
						}
					}
				}

				.total-cost {
					display: flex;
					justify-content: space-between;
					padding: 0 48px;

					&.overflow {
						h3 {
							color: $budget-bar-overflow-color;
						}
					}
				}

				.payment-method {
					padding: 0px 48px;
					display: flex;
					flex-direction: column;
					width: 100%;
					border-bottom: 1px solid #dfe9f4;
				}
				.alert-payment {
					color: $requiredFieldAsterisk;
				}

				.payment-button-container {
					padding-top: 32px;
					display: flex;
					align-items: center;
					justify-content: center;
					flex-direction: column;
				}
			}
		}
	}

	.discard-panel {
		text-align: left;
		padding-left: 48px;
		.btn {
			padding: 0 0 20px 0;
		}
	}
}
</style>
